import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectUser, selectGreenhouse, useGreenhouseActions } from 'state';
import { API, varietyRequests } from '../../api';
import {
  Container,
  SettingContainer,
  SettingsContainer,
  Table,
  TableCell,
  TableHeader,
  TableRow
} from './GoProCloudSettings.styles';
import { StyledInputText } from '../../components/_shared/Input';
import { StyledButton } from '../../components/FileUploader/FileUploader.styles';
import { Header } from '../Account/components/PageWrapper/PageWrapper.styles';
import { GreenhouseList } from '../../components/GreenhouseList';
import { StyledSelect } from '../Scanning/Scanning.styles';
import { Icon } from '../../components/_shared/Icon';
import { toast } from 'react-toastify';

const GoProCloudSettings = () => {
  const user = useSelector(selectUser);
  const customer = user?.customer?.[0]?.external_id;
  const { selectedGreenhouse, selectedGreenhouseExternalID } =
    useSelector(selectGreenhouse);
  const [isValid, setIsValid] = useState(false);
  const [configList, setConfigList] = useState([]);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [configData, setConfigData] = useState(null);
  const [cameraGroups, setCameraGroups] = useState({});
  const [expandedGroups, setExpandedGroups] = useState({});
  const [login, setLogin] = useState('');
  const updateConfigData = (field, value) => {
    if (field === 'login') {
      setLogin(value);
    }
    setConfigData({
      ...configData,
      [field]: value
    });
  };
  const validate = (value, cameraName, groupName) => {
    if (!/^C[0-9]{13}$/.test(value)) {
      if (groupName)
        toast.error(
          `Invalid Serial Number - "${value}" in Group - ${groupName}, Camera - ${cameraName}`
        );
      return false;
    }
    return true;
  };
  const updateCamera = (groupName, cameraName, field, value) => {
    setCameraGroups({
      ...cameraGroups,
      [groupName]: {
        ...cameraGroups[groupName],
        [cameraName]: {
          ...cameraGroups[groupName][cameraName],
          [field]: value
        }
      }
    });
  };

  const deleteCamera = (cameraName, groupName) => {
    const updated = {
      ...cameraGroups[groupName]
    };
    delete updated[cameraName];

    setCameraGroups({
      ...cameraGroups,
      [groupName]: updated
    });
  };
  const addGroup = () => {
    let index = 1;
    let name = `Group ${index}`;

    while (cameraGroups[name]) {
      index++;
      name = `Group ${index}`;
    }

    setIsValid(false);

    setCameraGroups(prev => ({
      ...prev,
      [name]: {}
    }));

    setExpandedGroups(prev => ({
      ...prev,
      [name]: true
    }));
  };
  const showGroup = groupName => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupName]: !prev[groupName]
    }));
  };

  const deleteGroup = groupName => {
    const updated = { ...cameraGroups };
    delete updated[groupName];

    setCameraGroups(updated);
  };

  const addCamera = groupName => {
    const group = cameraGroups[groupName];
    const cameraName = `Camera ${Object.keys(group).length + 1}`;
    setIsValid(false);
    setCameraGroups({
      ...cameraGroups,
      [groupName]: {
        ...group,
        [cameraName]: {
          serial_number: '',
          index: Object.keys(group).length + 1
        }
      }
    });
  };
  const handleSave = () => {
    const payload = {
      camera_groups: cameraGroups,
      ...(configData?.login && { login: configData.login }),
      ...(configData?.password && { password: configData.password })
    };

    API.config.updateConfigData(selectedConfig, payload);
  };
  useEffect(
    () =>
      API.config.getConfigs(customer, selectedGreenhouseExternalID).then(({ data }) => {
        setConfigList(data);
      }),
    [selectedGreenhouse]
  );
  useEffect(() => {
    if (Object.keys(cameraGroups).length === 0) {
      setIsValid(false);
      return;
    }
    if (login) {
      setIsValid(Boolean(login && configData?.password));
    }
    setIsValid(
      Object.values(cameraGroups).every(group => {
        const cameras = Object.values(group);
        if (cameras.length === 0) {
          return false;
        }
        const serials = cameras.map(c => c.serial_number);
        const hasDuplicates = serials.length !== new Set(serials).size;
        return !hasDuplicates && cameras.every(c => validate(c.serial_number));
      })
    );
  }, [cameraGroups, login, configData?.password]);
  return (
    <Container>
      <GreenhouseList />
      <StyledSelect
        items={configList.map(item => ({
          name: item.plant,
          value: item.config_id
        }))}
        onSelect={({ value }) => {
          setSelectedConfig(value);
          API.config.getConfigData(value).then(({ data }) => {
            setConfigData(data);
            setCameraGroups(data?.camera_groups || {});
          });
        }}
        value={selectedConfig ? selectedConfig : 'Select Plant'}
        placeholder={'Select Plant'}
      ></StyledSelect>
      {configData && (
        <>
          <SettingsContainer>
            {cameraGroups && (
              <SettingContainer>
                <Header> Camera Groups</Header>
                {Object.entries(cameraGroups).map(([groupName, cameras]) => (
                  <div
                    style={{
                      border: '1px solid #ccc',
                      padding: 16,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '1em',
                      marginBottom: 10,
                      borderRadius: 10
                    }}
                    key={groupName}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '3em'
                      }}
                    >
                      <Header>{groupName}</Header>
                      <div>
                        <StyledButton
                          disabled={Object.keys(cameraGroups).length === 1}
                          onClick={() => deleteGroup(groupName)}
                        >
                          <Icon>{'Delete'}</Icon>
                        </StyledButton>
                        <StyledButton
                          title='Show/Hide Details'
                          onClick={() => {
                            showGroup(groupName);
                          }}
                        >
                          <Icon width={20} height={20}>
                            {expandedGroups[groupName] ? 'ExpandUp' : 'ExpandDown'}
                          </Icon>
                        </StyledButton>
                      </div>
                    </div>
                    {expandedGroups[groupName] && (
                      <>
                        <Table>
                          <thead>
                            <TableRow>
                              <TableHeader>Name</TableHeader>
                              <TableHeader>Serial Number</TableHeader>
                              <TableHeader>Camera Index</TableHeader>
                              <TableHeader></TableHeader>
                            </TableRow>
                          </thead>
                          <tbody>
                            {Object.entries(cameras).map(([cameraName, camera]) => (
                              <TableRow key={cameraName + groupName}>
                                <TableCell>{cameraName}</TableCell>
                                <TableCell>
                                  <StyledInputText
                                    style={{ fontSize: '1rem' }}
                                    pattern='^C[0-9]{13}$'
                                    placeholder='Serial Number'
                                    value={camera.serial_number}
                                    onChange={e =>
                                      updateCamera(
                                        groupName,
                                        cameraName,
                                        'serial_number',
                                        e.target.value
                                      )
                                    }
                                    onBlur={e =>
                                      validate(e.target.value, cameraName, groupName)
                                    }
                                  />
                                </TableCell>
                                <TableCell>
                                  <StyledInputText
                                    type='number'
                                    disabled={true}
                                    style={{ fontSize: '1rem' }}
                                    value={camera.index}
                                    onChange={e =>
                                      updateCamera(
                                        groupName,
                                        cameraName,
                                        'index',
                                        Number(e.target.value)
                                      )
                                    }
                                  />
                                </TableCell>
                                <TableCell>
                                  <StyledButton
                                    disabled={
                                      Object.keys(cameraGroups).length === 1 &&
                                      cameraGroups[groupName].length === 1
                                    }
                                    onClick={() => deleteCamera(cameraName, groupName)}
                                  >
                                    <Icon>{'Delete'}</Icon>
                                  </StyledButton>
                                </TableCell>
                              </TableRow>
                            ))}
                          </tbody>
                        </Table>
                        <StyledButton
                          style={{ margin: 'auto', width: '25%' }}
                          isActive={true}
                          onClick={() => addCamera(groupName)}
                        >
                          Add Camera
                        </StyledButton>
                      </>
                    )}
                  </div>
                ))}

                <StyledButton onClick={addGroup}>Add Group</StyledButton>
              </SettingContainer>
            )}
            <SettingContainer>
              <Header> GoPRO Cloud Auth Info</Header>
              <div>
                <Header>Login</Header>
                <StyledInputText
                  style={{ fontSize: '1rem' }}
                  placeholder='Gopro Cloud Login'
                  value={configData.login}
                  onChange={e => updateConfigData('login', e.target.value)}
                />
              </div>
              <div>
                <Header>Password</Header>
                <StyledInputText
                  type={'password'}
                  style={{ fontSize: '1rem' }}
                  placeholder='Gopro Cloud Password'
                  value={configData.password}
                  onChange={e => updateConfigData('password', e.target.value)}
                />
              </div>
            </SettingContainer>
          </SettingsContainer>
          <StyledButton
            style={{ margin: 'auto', width: '25%' }}
            isActive={isValid}
            disabled={!isValid}
            onClick={handleSave}
          >
            Save
          </StyledButton>
        </>
      )}
    </Container>
  );
};

export default GoProCloudSettings;
